import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCreditCard, faSearch, faQuestionCircle, faFileInvoice, 
    faWallet, faReceipt, faExclamationTriangle, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface BillingFAQ {
    id: string;
    question: string;
    answer: string;
    category: 'payments' | 'invoicing' | 'subscriptions' | 'refunds' | 'taxes';
}

interface BillingResource {
    id: string;
    title: string;
    description: string;
    type: 'guide' | 'faq' | 'video' | 'form';
    url: string;
}

const BillingHelp: React.FC = () => {
    const [faqs, setFaqs] = useState<BillingFAQ[]>([]);
    const [resources, setResources] = useState<BillingResource[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Billing Help - Uzima Healthcare";
        loadClinicData();
        loadBillingData();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadBillingData = () => {
        // Mock billing FAQ data
        const mockFAQs: BillingFAQ[] = [
            {
                id: 'BILL-FAQ-001',
                question: 'How do I update my payment method?',
                answer: 'To update your payment method, go to Billing Settings in your dashboard. Click on "Payment Methods" and then "Add New Payment Method". You can add credit cards, debit cards, or bank accounts. Make sure to set the new payment method as default if needed.',
                category: 'payments'
            },
            {
                id: 'BILL-FAQ-002',
                question: 'When will I be charged for my subscription?',
                answer: 'Subscription charges occur on the same date each month as your initial signup. For example, if you signed up on the 15th, you will be charged on the 15th of each month. You will receive an email notification 3 days before the charge.',
                category: 'subscriptions'
            },
            {
                id: 'BILL-FAQ-003',
                question: 'How do I download my invoices?',
                answer: 'You can download invoices from the Billing section of your dashboard. Go to "Invoices" and click the download button next to any invoice. Invoices are available in PDF format and can be downloaded at any time.',
                category: 'invoicing'
            },
            {
                id: 'BILL-FAQ-004',
                question: 'What is your refund policy?',
                answer: 'We offer a 30-day money-back guarantee for new subscriptions. For existing customers, refunds are considered on a case-by-case basis. Contact our billing support team to discuss refund options for your specific situation.',
                category: 'refunds'
            },
            {
                id: 'BILL-FAQ-005',
                question: 'Are there any taxes on my subscription?',
                answer: 'Taxes are calculated based on your billing address and local tax regulations. Sales tax, VAT, or other applicable taxes will be added to your subscription fee. You can view the tax breakdown on your invoice.',
                category: 'taxes'
            },
            {
                id: 'BILL-FAQ-006',
                question: 'How do I cancel my subscription?',
                answer: 'To cancel your subscription, go to Billing Settings and click "Cancel Subscription". You can choose to cancel immediately or at the end of your current billing period. Your access will continue until the end of the paid period.',
                category: 'subscriptions'
            }
        ];

        // Mock billing resources
        const mockResources: BillingResource[] = [
            {
                id: 'BILL-RES-001',
                title: 'Payment Methods Guide',
                description: 'Complete guide to setting up and managing payment methods',
                type: 'guide',
                url: '/billing/payment-methods-guide'
            },
            {
                id: 'BILL-RES-002',
                title: 'Understanding Your Invoice',
                description: 'Video tutorial explaining invoice components and charges',
                type: 'video',
                url: '/billing/invoice-tutorial'
            },
            {
                id: 'BILL-RES-003',
                title: 'Subscription Management',
                description: 'How to upgrade, downgrade, or cancel your subscription',
                type: 'guide',
                url: '/billing/subscription-management'
            },
            {
                id: 'BILL-RES-004',
                title: 'Tax Information Form',
                description: 'Form to update your tax information and billing address',
                type: 'form',
                url: '/billing/tax-information-form'
            }
        ];

        setFaqs(mockFAQs);
        setResources(mockResources);
    };

    const filteredFAQs = faqs.filter(faq => {
        const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || faq.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'payments': return faCreditCard;
            case 'invoicing': return faFileInvoice;
            case 'subscriptions': return faReceipt;
            case 'refunds': return faExclamationTriangle;
            case 'taxes': return faFileInvoice;
            default: return faQuestionCircle;
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'payments': return 'primary';
            case 'invoicing': return 'info';
            case 'subscriptions': return 'success';
            case 'refunds': return 'warning';
            case 'taxes': return 'secondary';
            default: return 'secondary';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'guide': return faFileInvoice;
            case 'video': return faQuestionCircle;
            case 'form': return faReceipt;
            default: return faFileInvoice;
        }
    };

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'guide': return 'primary';
            case 'video': return 'danger';
            case 'form': return 'success';
            default: return 'secondary';
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faCreditCard} className="me-2 text-primary" />
                                    Billing Help
                                </h2>
                                <p className="text-muted mb-0">
                                    Get help with billing, payments, and subscriptions for {clinicName}
                                </p>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <FontAwesomeIcon icon={faCreditCard} className="text-primary fs-1 mb-2" />
                                        <h5 className="mb-1">Payments</h5>
                                        <p className="text-muted mb-0">Manage payment methods</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <FontAwesomeIcon icon={faFileInvoice} className="text-success fs-1 mb-2" />
                                        <h5 className="mb-1">Invoices</h5>
                                        <p className="text-muted mb-0">View and download</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <FontAwesomeIcon icon={faReceipt} className="text-info fs-1 mb-2" />
                                        <h5 className="mb-1">Subscriptions</h5>
                                        <p className="text-muted mb-0">Manage your plan</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-0 shadow-sm text-center">
                                    <div className="card-body">
                                        <FontAwesomeIcon icon={faWallet} className="text-warning fs-1 mb-2" />
                                        <h5 className="mb-1">Wallet</h5>
                                        <p className="text-muted mb-0">Account balance</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="row mb-4">
                            <div className="col-lg-8">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search billing questions..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4">
                                <select
                                    className="form-select"
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                >
                                    <option value="all">All Categories</option>
                                    <option value="payments">Payments</option>
                                    <option value="invoicing">Invoicing</option>
                                    <option value="subscriptions">Subscriptions</option>
                                    <option value="refunds">Refunds</option>
                                    <option value="taxes">Taxes</option>
                                </select>
                            </div>
                        </div>

                        <div className="row">
                            {/* FAQs */}
                            <div className="col-lg-8">
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                                            Frequently Asked Questions
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {filteredFAQs.map((faq) => (
                                            <div key={faq.id} className="border-bottom pb-3 mb-3">
                                                <div className="d-flex align-items-start">
                                                    <div className="flex-shrink-0 me-3">
                                                        <FontAwesomeIcon 
                                                            icon={getCategoryIcon(faq.category)} 
                                                            className={`text-${getCategoryColor(faq.category)}`}
                                                        />
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-2">{faq.question}</h6>
                                                        <p className="text-muted mb-2">{faq.answer}</p>
                                                        <span className={`badge bg-${getCategoryColor(faq.category)} bg-opacity-20 text-${getCategoryColor(faq.category)}`}>
                                                            {faq.category.toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Resources */}
                            <div className="col-lg-4">
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Helpful Resources</h6>
                                    </div>
                                    <div className="card-body">
                                        {resources.map((resource) => (
                                            <div key={resource.id} className="d-flex align-items-start mb-3">
                                                <div className="flex-shrink-0 me-3">
                                                    <FontAwesomeIcon 
                                                        icon={getTypeIcon(resource.type)} 
                                                        className={`text-${getTypeColor(resource.type)}`}
                                                    />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1">{resource.title}</h6>
                                                    <p className="text-muted small mb-2">{resource.description}</p>
                                                    <span className={`badge bg-${getTypeColor(resource.type)} bg-opacity-20 text-${getTypeColor(resource.type)}`}>
                                                        {resource.type.toUpperCase()}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Contact Support */}
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Need More Help?</h6>
                                    </div>
                                    <div className="card-body text-center">
                                        <FontAwesomeIcon icon={faQuestionCircle} className="text-primary fs-1 mb-3" />
                                        <h6 className="mb-2">Contact Billing Support</h6>
                                        <p className="text-muted small mb-3">
                                            Our billing team is here to help with any questions or issues.
                                        </p>
                                        <button className="btn btn-primary btn-sm">
                                            Contact Support
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default BillingHelp;
